import { CircleAlert, Mail, MessageSquare, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Inbox } from '../../api/types';
import { SmsDefaultForm } from '../../components/SmsDefaultForm';
import { identityLine, isManagedInbox, needsReauth, providerLine } from '../../lib/channelMeta';
import { smsDefaultStatus } from '../../lib/smsDefault';
import { type SettingsSection, SETTINGS_SECTIONS, campaignSenders, healthChip } from './campaignSettingsTools';
import { useComms } from './context';
import { OptOutSection } from './OptOutSection';
import { openSettingsTab } from './settingsTab';
import { SenderHealthSection } from './SenderHealthSection';
import { LinkDomainSection } from './ShortLinkDomain';
import { useConfigNav } from './useConfigNav';

/**
 * Campaigns → Settings: everything a campaign leans on that is not the
 * campaign itself, one section at a time beside a nav that says what each one
 * holds. Most of it is owned elsewhere — a mailbox's sign-in and health are
 * that mailbox's, the default SMS number is the tenant's — and is gathered
 * here because a campaign is where it matters, through the same endpoints and
 * the same rules as its home screen, so the two can never disagree.
 */
export function CampaignSettings({
  section,
  focusInboxId,
  onSection,
  onOpenCampaign,
}: {
  section: SettingsSection;
  /** A mailbox the Senders list asked Sender health to bring into view. */
  focusInboxId: number | null;
  onSection: (section: SettingsSection, inboxId?: number) => void;
  onOpenCampaign: (id: number) => void;
}) {
  return (
    <div className="cpset">
      <nav className="cpset-card cpset-nav" aria-label="Campaign settings">
        {SETTINGS_SECTIONS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`cpset-nav-item${section === item.id ? ' on' : ''}`}
            aria-current={section === item.id ? 'page' : undefined}
            onClick={() => onSection(item.id)}
          >
            <b>{item.title}</b>
            <span>{item.sub}</span>
          </button>
        ))}
      </nav>
      <div className="cpset-main">
        {section === 'senders' && <SendersSection onSection={onSection} />}
        {section === 'health' && (
          <Titled title="Sender health">
            <SenderHealthSection focusInboxId={focusInboxId} />
          </Titled>
        )}
        {section === 'links' && <LinkDomainSection />}
        {section === 'optout' && (
          <Titled title="Opting out">
            <OptOutSection onOpenCampaign={onOpenCampaign} />
          </Titled>
        )}
        {section === 'sms' && <AutomatedTextsSection />}
      </div>
    </div>
  );
}

function Titled({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <header className="cpset-head">
        <h2>{title}</h2>
      </header>
      {children}
    </>
  );
}

/**
 * Where campaigns can send from, by channel — the builder's own list — with
 * what is wrong with each said on its row, and the way to fix it one press
 * away: the mailbox's own settings, or its health below.
 */
function SendersSection({ onSection }: { onSection: (section: SettingsSection, inboxId?: number) => void }) {
  const { source, inboxes, inboxesLoading, capabilities, canReply } = useComms();
  const configNav = useConfigNav();
  const { email, sms } = campaignSenders(inboxes);
  // Permissive while /meta is in flight — the server decides either way.
  const mayConnect = capabilities?.access.features.connectChannels !== false;
  const connect = () => openSettingsTab('/connect', source.embedded);

  const row = (inbox: Inbox, kind: 'email' | 'sms') => {
    const health = inbox.channelType === 'email' && isManagedInbox(inbox) ? inbox.channel.senderHealth : undefined;
    const chip = kind === 'email' && health !== undefined ? healthChip(health) : null;
    const cap = inbox.channelType === 'email' && isManagedInbox(inbox) ? inbox.channel.dailySendLimit : undefined;
    return (
      <li key={inbox.id} className="cpset-sender">
        <span className="cpset-sender-icon" aria-hidden>
          {kind === 'email' ? <Mail size={16} /> : <MessageSquare size={16} />}
        </span>
        <div className="cpset-sender-text">
          <b>{identityLine(inbox)}</b>
          <span>
            {providerLine(inbox)}
            {inbox.name && inbox.name !== identityLine(inbox) ? ` · ${inbox.name}` : ''}
          </span>
        </div>
        <div className="cpset-sender-chips">
          {needsReauth(inbox) && <span className="cpset-chip risk">Needs signing in</span>}
          {!canReply(inbox.id) && <span className="cpset-chip na">You can’t send from it</span>}
          {chip && (
            <button
              type="button"
              className={`cpset-chip ${chip.state} cpset-chip-btn`}
              title="See its sender health"
              onClick={() => onSection('health', inbox.id)}
            >
              {chip.label}
            </button>
          )}
          {cap != null && <span className="cpset-chip na">{cap === 0 ? 'Sending off' : `${cap.toLocaleString()} a day`}</span>}
          {inbox.defaultSmsSender && (
            <button type="button" className="cpset-chip ok cpset-chip-btn" onClick={() => onSection('sms')}>
              Automated texts
            </button>
          )}
        </div>
        <button type="button" className="cbtn" onClick={() => configNav(`/inboxes/${inbox.id}`)}>
          Settings
        </button>
      </li>
    );
  };

  return (
    <>
      <header className="cpset-head cpset-head-row">
        <div>
          <h2>Senders</h2>
          <p>
            The mailboxes and numbers a campaign can send from. Each one’s sign-in, limits and inbox settings are its
            own; what stands in the way of a campaign is said on its row.
          </p>
        </div>
        {mayConnect && (
          <button type="button" className="cbtn" onClick={connect}>
            <Plus size={13} />
            Connect a mailbox or number
          </button>
        )}
      </header>

      <section className="cpset-card" aria-labelledby="cpset-senders-email">
        <div className="cpset-pad cpset-section-top">
          <h3 id="cpset-senders-email">Email</h3>
          <span className="cpset-hint">Campaign emails go out from the mailbox itself, as if somebody had sent them.</span>
        </div>
        {inboxesLoading && !email.length ? (
          <div className="cpset-pad cpset-muted">Loading…</div>
        ) : email.length ? (
          <ul className="cpset-senders">{email.map((inbox) => row(inbox, 'email'))}</ul>
        ) : (
          <div className="cpset-pad cpset-muted">No mailbox is connected, so email campaigns have nothing to send from.</div>
        )}
      </section>

      <section className="cpset-card" aria-labelledby="cpset-senders-sms">
        <div className="cpset-pad cpset-section-top">
          <h3 id="cpset-senders-sms">Text messages</h3>
          <span className="cpset-hint">
            A RingCentral number or a Twilio SMS number. A WhatsApp sender cannot send a campaign: it needs approved
            templates.
          </span>
        </div>
        {inboxesLoading && !sms.length ? (
          <div className="cpset-pad cpset-muted">Loading…</div>
        ) : sms.length ? (
          <ul className="cpset-senders">{sms.map((inbox) => row(inbox, 'sms'))}</ul>
        ) : (
          <div className="cpset-pad cpset-muted">No SMS number is connected, so text campaigns have nothing to send from.</div>
        )}
      </section>
    </>
  );
}

/**
 * The tenant's default SMS sender — the number dispatch's own automated texts
 * leave from. Not a campaign setting (a campaign picks its own sender), but the
 * one other place an operator decides which number customers are texted from,
 * so it sits here beside the senders rather than only on the channel list.
 * The form is the channel list's own (components/SmsDefaultForm), rules and all.
 */
function AutomatedTextsSection() {
  const { source, inboxes, capabilities, reloadInboxes } = useComms();
  const configNav = useConfigNav();
  const mayConnect = capabilities?.access.features.connectChannels !== false;
  const none = smsDefaultStatus(inboxes).kind === 'none';
  return (
    <>
      <header className="cpset-head">
        <h2>Automated texts</h2>
        <p>
          The number the dispatch system’s own texts leave from. Campaigns do not use it — each one picks its own
          sender — but it is the other place you decide which number customers are texted from.
        </p>
      </header>
      {none ? (
        <section className="cpset-card cpset-pad cpset-empty">
          <CircleAlert size={18} aria-hidden />
          <b>No number can send automated texts yet</b>
          <span className="cpset-hint">
            Connect a RingCentral number, or a Twilio number set up for SMS, and it becomes the default straight
            away.
          </span>
          {mayConnect && (
            <button type="button" className="cbtn" onClick={() => openSettingsTab('/connect', source.embedded)}>
              <Plus size={13} />
              Connect a number
            </button>
          )}
        </section>
      ) : (
        <SmsDefaultForm
          inboxes={inboxes}
          canChange={mayConnect}
          onOpenInbox={(id) => configNav(`/inboxes/${id}`)}
          reload={reloadInboxes}
        />
      )}
    </>
  );
}
